import {
  aiService,
  type TAnalysisStatus,
  type TCompanyReadinessResponse,
  type TInvestorRecommendationResponse,
} from "@/api/ai";
import type { TApiServiceError } from "@/api/apiService";
import { AppCard, useToast } from "@/components/ui";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { extractErrorMessage } from "@/utils/errorUtils";
import { sanitizeUpstreamErrorMessage } from "@/utils/sanitizeUpstreamErrorMessage";

import { AlertCircle, CheckCircle2, LoaderCircle, TrendingUp, XCircle } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { ReadinessDashboard } from "./ReadinessDashboard";
import { ProcessingCard } from "./readiness/ProcessingCard";
import {
  normalizeFundingRecommendationsFromApi,
  normalizeReadinessCategoriesFromApi,
} from "./readiness/normalizeReadinessApi";
import type { TInvestorReadinessAssessment, TReadinessData } from "./readiness/types";

/**
 * Extract JSON object from text that may contain embedded JSON.
 * Looks for JSON structures in the text and tries to parse them.
 * Handles cases where JSON is embedded at the end of descriptive text.
 */
function extractJsonFromText(text: string): Record<string, unknown> | null {
  if (!text || typeof text !== "string") return null;

  // Try to find JSON object in the text (look for { ... } pattern)
  // First, try parsing the entire text as JSON
  try {
    const parsed = JSON.parse(text) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // Not valid JSON, continue to search for embedded JSON
  }

  // Look for the pattern "Here's a structured assessment:" which indicates JSON follows
  const assessmentMarker = "Here's a structured assessment:";
  const markerIndex = text.indexOf(assessmentMarker);
  if (markerIndex !== -1) {
    // Extract from after the marker
    const jsonCandidate = text.substring(markerIndex + assessmentMarker.length).trim();
    // Remove leading newlines and whitespace
    const cleaned = jsonCandidate.replace(/^\s*\n+\s*/, "");
    try {
      const parsed = JSON.parse(cleaned) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      // Continue to next attempt
    }
  }

  // Fallback: Find the last occurrence of "{" that might be the start of the assessment JSON
  const lastBraceIndex = text.lastIndexOf("{");
  if (lastBraceIndex === -1) return null;

  // Try to extract and parse from the last brace to the end
  const jsonCandidate = text.substring(lastBraceIndex);
  try {
    const parsed = JSON.parse(jsonCandidate) as unknown;
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // Try to find investor_readiness_assessment pattern using RegExp.exec()
    const assessmentRegex = /"investor_readiness_assessment"\s*:\s*(\{[\s\S]*\})/;
    const assessmentMatch = assessmentRegex.exec(jsonCandidate);
    if (assessmentMatch?.[1]) {
      try {
        const parsed = JSON.parse(assessmentMatch[1]) as unknown;
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          return { investor_readiness_assessment: parsed };
        }
      } catch {
        // Continue to next attempt
      }
    }
  }

  return null;
}

function sanitizeAnalysisErrorMessage(msg: string): string {
  return sanitizeUpstreamErrorMessage(
    msg,
    "Something went wrong during analysis. Try again in a moment."
  );
}

/**
 * Parse readiness data from API response.
 * Handles both readiness_data and readiness_summary (JSON string) formats.
 * The assessment data may be embedded as JSON strings within text fields.
 * Returns both the parsed data and any message text found.
 */
function parseReadinessData(
  readinessData: Record<string, unknown> | null,
  readinessSummary: string | null
): { data: TReadinessData | null; message: string | null } {
  // Type guard for investor_readiness_assessment
  const isValidAssessment = (value: unknown): value is TInvestorReadinessAssessment => {
    if (!value || typeof value !== "object") return false;
    const assessment = value as Record<string, unknown>;
    return (
      typeof assessment.readiness_score === "number" ||
      typeof assessment.readiness_level === "number" ||
      typeof assessment.summary === "string" ||
      Array.isArray(assessment.strengths) ||
      Array.isArray(assessment.recommendations)
    );
  };

  // Helper to extract message text from various structures
  const extractMessage = (): string | null => {
    // Try readiness_summary first
    if (readinessSummary) {
      try {
        const parsedSummary = JSON.parse(readinessSummary) as Record<string, unknown>;
        if (parsedSummary.company_readiness && typeof parsedSummary.company_readiness === "string") {
          return parsedSummary.company_readiness;
        }
      } catch {
        // Continue to other sources
      }
    }

    // Try readiness_data
    if (readinessData?.company_readiness) {
      const companyReadiness = readinessData.company_readiness as Record<string, unknown>;
      if (companyReadiness.company_readiness && typeof companyReadiness.company_readiness === "string") {
        return companyReadiness.company_readiness;
      }
    }

    return null;
  };

  // First, try to parse readiness_summary (JSON string)
  if (readinessSummary) {
    try {
      const parsedSummary = JSON.parse(readinessSummary) as Record<string, unknown>;

      // Check if it has the investor_readiness_assessment directly as an object
      if (
        parsedSummary.investor_readiness_assessment &&
        isValidAssessment(parsedSummary.investor_readiness_assessment)
      ) {
        return {
          data: {
            company_readiness: {
              investor_readiness_assessment: parsedSummary.investor_readiness_assessment,
            },
          },
          message: null,
        };
      }

      // Check if company_readiness is a string that contains embedded JSON
      if (parsedSummary.company_readiness && typeof parsedSummary.company_readiness === "string") {
        const extractedJson = extractJsonFromText(parsedSummary.company_readiness);
        if (
          extractedJson?.investor_readiness_assessment &&
          isValidAssessment(extractedJson.investor_readiness_assessment)
        ) {
          return {
            data: {
              company_readiness: {
                investor_readiness_assessment: extractedJson.investor_readiness_assessment,
              },
            },
            message: null,
          };
        }
      }

      // Check if it's nested in company_readiness as an object
      if (parsedSummary.company_readiness && typeof parsedSummary.company_readiness === "object") {
        const companyReadiness = parsedSummary.company_readiness as Record<string, unknown>;
        if (
          companyReadiness.investor_readiness_assessment &&
          isValidAssessment(companyReadiness.investor_readiness_assessment)
        ) {
          return {
            data: {
              company_readiness: {
                investor_readiness_assessment: companyReadiness.investor_readiness_assessment,
              },
            },
            message: null,
          };
        }
      }
    } catch (error) {
      // If parsing fails, fall through to readiness_data
      console.warn("[investor-readiness] Failed to parse readiness_summary", { error });
    }
  }

  // Fallback to readiness_data
  if (readinessData) {
    // Check if company_readiness exists and is an object
    if (readinessData.company_readiness && typeof readinessData.company_readiness === "object") {
      const companyReadiness = readinessData.company_readiness as Record<string, unknown>;

      // Check if it has investor_readiness_assessment directly
      if (
        companyReadiness.investor_readiness_assessment &&
        isValidAssessment(companyReadiness.investor_readiness_assessment)
      ) {
        return {
          data: {
            company_readiness: {
              investor_readiness_assessment: companyReadiness.investor_readiness_assessment,
            },
          },
          message: null,
        };
      }

      // Check if company_readiness.company_readiness is a string with embedded JSON
      if (companyReadiness.company_readiness && typeof companyReadiness.company_readiness === "string") {
        const extractedJson = extractJsonFromText(companyReadiness.company_readiness);
        if (
          extractedJson?.investor_readiness_assessment &&
          isValidAssessment(extractedJson.investor_readiness_assessment)
        ) {
          return {
            data: {
              company_readiness: {
                investor_readiness_assessment: extractedJson.investor_readiness_assessment,
              },
            },
            message: null,
          };
        }
      }
    }

    // If readiness_data itself has investor_readiness_assessment at root
    if (readinessData.investor_readiness_assessment && isValidAssessment(readinessData.investor_readiness_assessment)) {
      return {
        data: {
          company_readiness: {
            investor_readiness_assessment: readinessData.investor_readiness_assessment,
          },
        },
        message: null,
      };
    }

    // New API shape: company_readiness.categories (nested maturity + performance) with optional summary
    if (readinessData.company_readiness && typeof readinessData.company_readiness === "object") {
      const companyReadiness = readinessData.company_readiness as Record<string, unknown>;
      const apiCategories = companyReadiness.categories;
      if (apiCategories && typeof apiCategories === "object" && !Array.isArray(apiCategories)) {
        const categories = normalizeReadinessCategoriesFromApi(apiCategories);
        const summary = companyReadiness.summary as Record<string, unknown> | undefined;
        const averageScore = summary && typeof summary.average_score === "number" ? summary.average_score : undefined;
        const totalCategories =
          summary && typeof summary.total_categories === "number" ? summary.total_categories : undefined;
        const errors = Array.isArray(companyReadiness.errors) ? (companyReadiness.errors as string[]) : undefined;
        const minimalAssessment: TInvestorReadinessAssessment | undefined =
          averageScore !== undefined
            ? {
                summary:
                  totalCategories !== undefined
                    ? `Average score: ${Math.round(averageScore)} across ${totalCategories} categories.`
                    : `Average score: ${Math.round(averageScore)}.`,
                // average_score is already on the canonical 0-100 scale
                readiness_score: Math.min(100, Math.max(0, Math.round(averageScore))),
              }
            : undefined;
        return {
          data: {
            company_readiness: {
              run_id: companyReadiness.run_id as string | undefined,
              investor_readiness_assessment: minimalAssessment,
            },
            categories,
            funding_recommendations: normalizeFundingRecommendationsFromApi(
              companyReadiness.funding_recommendations
            ),
            errors,
            llm_usage: companyReadiness.llm_usage as Record<string, unknown> | undefined,
          },
          message: null,
        };
      }
    }
  }

  // If no assessment found, return the message if available
  const message = extractMessage();
  return { data: null, message };
}


function extractReadinessRunId(readiness: TCompanyReadinessResponse | null): string | null {
  if (!readiness?.readiness_data || typeof readiness.readiness_data !== "object") return null;
  const companyReadiness = readiness.readiness_data.company_readiness;
  if (companyReadiness && typeof companyReadiness === "object") {
    const runId = (companyReadiness as Record<string, unknown>).run_id;
    if (typeof runId === "string" && runId.trim()) return runId;
  }
  const topLevelRunId = readiness.readiness_data.run_id;
  return typeof topLevelRunId === "string" && topLevelRunId.trim() ? topLevelRunId : null;
}

type TInvestorReadinessFeatureProps = {
  companyId?: string | null;
  companyName?: string | null;
  sectionId?: string | null;
  onRequestTodoRefresh?: () => void;
};

export function InvestorReadinessFeature({
  companyId,
  sectionId,
  onRequestTodoRefresh,
}: TInvestorReadinessFeatureProps) {
  const [readiness, setReadiness] = useState<TCompanyReadinessResponse | null>(null);
  const [, setRecommendations] = useState<TInvestorRecommendationResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isReevaluating, setIsReevaluating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isReevaluationRun, setIsReevaluationRun] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const previousStatusRef = useRef<TAnalysisStatus | undefined>();
  const hasInitializedStatusRef = useRef(false);
  const { toast } = useToast();

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (!companyId) return;

    // Stop any existing polling
    stopPolling();

    // Poll every 10 seconds; fetch both readiness and recommendations until both are terminal.
    // Hard cap the number of polls so a backend that never reports a terminal status can't
    // pin this loop open forever (the bug this replaces). The cap must comfortably outlast a
    // full re-evaluation (observed up to ~30 min) so the poll doesn't die mid-run and then get
    // restarted on the next mount — which is what made the calls look "non-stop".
    let pollCount = 0;
    const POLL_INTERVAL_MS = 10000;
    const MAX_POLLS = 180; // ~30 minutes at 10s
    const interval = setInterval(() => {
      if (!companyId) return;
      pollCount += 1;

      void (async () => {
        const [readinessResult, recommendationsResult] = await Promise.all([
          aiService.getCompanyReadiness(companyId),
          aiService.getInvestorRecommendations(companyId),
        ]);

        if (!readinessResult.error && readinessResult.data?.data) {
          setReadiness(readinessResult.data.data);
        }
        if (!recommendationsResult.error && recommendationsResult.data?.data) {
          setRecommendations(recommendationsResult.data.data);
        }

        // A run is still "active" only while it explicitly reports in_progress/pending.
        // Any other status (completed, failed, or absent) is terminal. If a poll request
        // itself errored we keep polling, so a transient network blip can't strand the UI on
        // a spinner — but we never keep polling on an unexpected/unknown status, which is what
        // let the loop run forever before.
        const readinessOk = !readinessResult.error && Boolean(readinessResult.data?.data);
        const recsOk = !recommendationsResult.error && Boolean(recommendationsResult.data?.data);
        const readinessStatus = readinessResult.data?.data?.status;
        const recsStatus = recommendationsResult.data?.data?.status;
        const readinessActive =
          !readinessOk || readinessStatus === "in_progress" || readinessStatus === "pending";
        const recsActive = recsOk && (recsStatus === "in_progress" || recsStatus === "pending");

        if ((!readinessActive && !recsActive) || pollCount >= MAX_POLLS) {
          stopPolling();
        }
      })();
    }, POLL_INTERVAL_MS);

    pollingIntervalRef.current = interval;
  }, [companyId, stopPolling]);

  // Fetch readiness and investor recommendations when companyId changes
  useEffect(() => {
    if (!companyId) {
      return;
    }

    const fetchReadiness = async () => {
      setIsLoading(true);
      setErrorMessage(null);
      const [readinessResult, recommendationsResult] = await Promise.all([
        aiService.getCompanyReadiness(companyId),
        aiService.getInvestorRecommendations(companyId),
      ]);

      const { data: readinessData, error: fetchError } = readinessResult;
      if (fetchError || !readinessData?.data) {
        setErrorMessage(fetchError ? await extractErrorMessage(fetchError) : "Failed to load readiness data. Please try again.");
        setIsLoading(false);
        return;
      }

      setReadiness(readinessData.data);
      if (recommendationsResult.data?.data) {
        setRecommendations(recommendationsResult.data.data);
      } else {
        setRecommendations(null);
      }
      setIsLoading(false);

      const readinessInProgress =
        readinessData.data?.status === "in_progress" || readinessData.data?.status === "pending";
      const recsInProgress =
        recommendationsResult.data?.data?.status === "in_progress" ||
        recommendationsResult.data?.data?.status === "pending";

      if (readinessInProgress || recsInProgress) {
        startPolling();
      } else {
        stopPolling();
      }
    };

    void fetchReadiness();

    return () => {
      stopPolling();
    };
  }, [companyId, startPolling, stopPolling]);

  useEffect(() => {
    const currentStatus = readiness?.status;
    if (!hasInitializedStatusRef.current) {
      hasInitializedStatusRef.current = true;
      previousStatusRef.current = currentStatus;
      return;
    }

    if (previousStatusRef.current !== currentStatus) {
      if (currentStatus === "completed") {
        onRequestTodoRefresh?.();
        if (isMinimized) {
          toast({ variant: "success", title: "Readiness analysis complete" });
          setIsMinimized(false);
        }
      }
      if (currentStatus === "completed" || currentStatus === "failed") {
        setIsReevaluationRun(false);
      }
      previousStatusRef.current = currentStatus;
    }
  }, [onRequestTodoRefresh, readiness?.status, isMinimized, toast]);

  const handleGenerate = () => {
    if (!companyId) return;

    void (async () => {
      setIsGenerating(true);
      setErrorMessage(null);

      const { data, error: generateError } = await aiService.generateCompanyReadiness(companyId);

      if (generateError || !data?.data) {
        setErrorMessage(
          generateError
            ? await extractErrorMessage(generateError)
            : "Failed to start readiness generation"
        );
        setIsGenerating(false);
        return;
      }

      setIsGenerating(false);

      // Start polling for status updates
      startPolling();

      // Also fetch immediately to get initial status
      const { data: readinessData } = await aiService.getCompanyReadiness(companyId);
      if (readinessData?.data) {
        setReadiness(readinessData.data);
      }
    })();
  };

  const handleReevaluate = () => {
    if (!companyId) return;

    setIsReevaluationRun(true);
    void (async () => {
      setIsReevaluating(true);
      setErrorMessage(null);

      const { data, error: reevaluateError } = await aiService.reevaluateCompanyReadiness(companyId, {
        section: "investor_readiness",
        runId: extractReadinessRunId(readiness) ?? undefined,
      });

      if (reevaluateError || !data?.data) {
        setErrorMessage(
          reevaluateError
            ? await extractErrorMessage(reevaluateError)
            : "Failed to re-evaluate maturity/performance"
        );
        setIsReevaluating(false);
        return;
      }

      setIsReevaluating(false);
      startPolling();

      // Refresh once immediately so UI reflects in_progress quickly.
      const { data: readinessData } = await aiService.getCompanyReadiness(companyId);
      if (readinessData?.data) {
        setReadiness(readinessData.data);
      }
    })();
  };

  const getStatusIcon = (status: TAnalysisStatus | undefined) => {
    switch (status) {
      case "completed":
        return <CheckCircle2 className="h-5 w-5 text-foreground" />;
      case "failed":
        return <XCircle className="h-5 w-5 text-destructive" />;
      case "in_progress":
      case "pending":
        return <LoaderCircle className="h-5 w-5 animate-spin text-primary" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: TAnalysisStatus | undefined) => {
    switch (status) {
      case "completed":
        return "Analysis Complete";
      case "failed":
        return "Analysis Failed";
      case "in_progress":
        return "Analysis in Progress";
      case "pending":
        return "Analysis Pending";
      default:
        return "Not Started";
    }
  };

  const hasSummaryPayload =
    typeof readiness?.readiness_summary === "string"
      ? readiness.readiness_summary.trim().length > 0
      : Boolean(readiness?.readiness_data);
  const canDownloadPdf = Boolean(companyId && readiness?.status === "completed" && hasSummaryPayload);

  useEffect(() => {
    if (canDownloadPdf) {
      setDownloadError(null);
    }
  }, [canDownloadPdf]);

  const handleDownloadPdf = useCallback(() => {
    if (!companyId || !canDownloadPdf) {
      if (!canDownloadPdf) {
        setDownloadError("Generate a readiness analysis before downloading the PDF.");
      }
      return;
    }

    setIsDownloadingPdf(true);
    setDownloadError(null);

    void (async () => {
      const { data, error } = await aiService.downloadCompanyReadinessReport(companyId);
      setIsDownloadingPdf(false);

      if (error || !data) {
        const statusCode = (error as TApiServiceError | undefined)?.response?.status;
        const defaultMessage = "Unable to download the readiness summary. Please try again.";
        setDownloadError(
          statusCode === 404 ? "Generate a readiness analysis before downloading the PDF." : defaultMessage
        );
        return;
      }

      const blobUrl = URL.createObjectURL(data.blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = data.filename ?? "investor_readiness_summary.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    })();
  }, [canDownloadPdf, companyId]);

  const isButtonDisabled =
    isLoading ||
    isGenerating ||
    isReevaluating ||
    readiness?.status === "in_progress" ||
    readiness?.status === "pending";
  const isCompleted = readiness?.status === "completed";
  const rawData = readiness?.readiness_data;
  const dataErrors = rawData && typeof rawData === "object" && "errors" in rawData ? rawData.errors : undefined;
  const hasDataGap = Array.isArray(dataErrors) && dataErrors.length > 0;
  // Distinguish "we couldn't read your documents" (a transient/system issue) from a
  // genuine data gap, so we don't silently pass off an incomplete score as final.
  const knowledgeBaseUnavailable =
    Array.isArray(dataErrors) &&
    dataErrors.some((e) =>
      /ragflow|knowledge base|api key|authentication|unavailable|circuit|connection|timeout|unreachable/i.test(
        String(e)
      )
    );

  // Re-run button only when investor readiness is completed and not in progress
  const showReevaluateOnly = readiness?.status === "completed";

  const inProgress =
    readiness?.status === "in_progress" || readiness?.status === "pending" || isGenerating || isReevaluating;

  if (companyId && inProgress) {
    return (
      <div className="flex h-full flex-col gap-4">
        <ProcessingCard
          variant={isReevaluationRun ? "reevaluation" : "analysis"}
          minimized={isMinimized}
          onNotifyWhenFinished={() => setIsMinimized(true)}
          onExpand={isMinimized ? () => setIsMinimized(false) : undefined}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-4">
      {companyId && (
        <AppCard className="p-4">
          <div className="mb-4 flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary sm:h-9 sm:w-9">
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-foreground sm:text-base md:text-lg">
                Company Readiness Analysis
              </h3>
              <p className="text-xs text-muted-foreground">
                Your readiness score across key investment categories based on your onboarding data.
              </p>
            </div>
          </div>

          {isLoading && !readiness ? (
            <div className="space-y-3">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-4/5" />
              <Skeleton className="mt-2 h-9 w-full rounded-lg" />
              <Skeleton className="mt-1 h-9 w-full rounded-lg" />
            </div>
          ) : (
            <>
              {showReevaluateOnly ? (
                <div className="space-y-2">
                  <Button
                    onClick={handleReevaluate}
                    disabled={isButtonDisabled}
                    variant="outline"
                    className="w-full whitespace-normal transition-transform duration-200 ease-in-out active:scale-[0.98]"
                    loading={isReevaluating}
                    error={!!errorMessage}
                  >
                    Re-run readiness analysis
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Re-evaluation runs in the background and can take about 8–15 minutes.
                  </p>
                </div>
              ) : (
                <Button
                  onClick={handleGenerate}
                  disabled={isButtonDisabled}
                  className="w-full whitespace-normal bg-primary text-primary-foreground transition-transform duration-200 ease-in-out hover:bg-primary/90 active:scale-[0.98]"
                  loading={isGenerating || readiness?.status === "in_progress" || readiness?.status === "pending"}
                  success={isCompleted}
                  error={!!errorMessage}
                >
                  {isCompleted ? "Analysis complete" : "Generate readiness analysis"}
                </Button>
              )}

              <Button
                onClick={handleDownloadPdf}
                disabled={!canDownloadPdf || isDownloadingPdf || isButtonDisabled}
                variant="outline"
                className="mt-3 w-full"
                loading={isDownloadingPdf}
              >
                Download readiness PDF
              </Button>

              {knowledgeBaseUnavailable ? (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-2.5 py-2 text-xs text-amber-700 dark:text-amber-400 sm:text-sm">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                  <span>
                    We couldn't read your uploaded documents for this analysis, so this score may be
                    incomplete. Please re-run the analysis in a moment.
                  </span>
                </div>
              ) : hasDataGap ? (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-border bg-muted px-2 py-2 text-xs text-foreground sm:text-sm">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground sm:h-4 sm:w-4" />
                  <span>Data gap — upload more documents to improve the average score and assessment quality.</span>
                </div>
              ) : null}

              {errorMessage && (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive sm:text-sm">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                  <span className="min-w-0 break-words">{errorMessage}</span>
                </div>
              )}

              {downloadError && (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive sm:text-sm">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4" />
                  <span className="min-w-0 break-words">{downloadError}</span>
                </div>
              )}

              {readiness && (
                <div className="mt-4 space-y-4">
                  <div className="flex min-w-0 flex-col gap-1 border-t border-border pt-4">
                    <div className="flex min-w-0 items-center gap-2 text-sm font-medium text-foreground">
                      {getStatusIcon(readiness.status)}
                      <span>{getStatusText(readiness.status)}</span>
                    </div>
                  </div>

                  {readiness.status === "completed" && (
                    <div className="mt-4">
                      {(() => {
                        const { data: parsedData, message: readinessMessage } = parseReadinessData(
                          readiness.readiness_data,
                          readiness.readiness_summary
                        );
                        const rawPayload =
                          readiness.readiness_data && typeof readiness.readiness_data === "object"
                            ? readiness.readiness_data
                            : null;
                        if (parsedData) {
                          return (
                            <ReadinessDashboard
                              readinessData={parsedData}
                              rawReadinessData={rawPayload}
                              message={null}
                              companyId={companyId ?? undefined}
                              sectionId={sectionId ?? undefined}
                            />
                          );
                        }
                        if (readinessMessage) {
                          return (
                            <ReadinessDashboard
                              readinessData={null}
                              rawReadinessData={rawPayload}
                              message={readinessMessage}
                              companyId={companyId ?? undefined}
                              sectionId={sectionId ?? undefined}
                            />
                          );
                        }
                        return (
                          <div className="rounded-lg border border-border bg-muted p-4">
                            <p className="text-sm italic text-muted-foreground">No readiness assessment data available</p>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {readiness.status === "failed" && readiness.error_message && (
                    <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4">
                      <h4 className="mb-2 text-sm font-semibold text-destructive">What went wrong</h4>
                      <p className="break-words text-sm text-destructive/80">{sanitizeAnalysisErrorMessage(readiness.error_message)}</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </AppCard>
      )}
    </div>
  );
}
